import React, { useState, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { Clock } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { ChartToggle, ChartType } from './ChartToggle';

interface HourlyScan {
  hour: number;
  count: number;
}

interface PeakHoursChartProps {
  data?: HourlyScan[];
  totalScans?: number;
  className?: string;
}

const chartConfig = {
  scans: {
    label: "Scans",
    color: "#F59E0B",
  },
};

export function PeakHoursChart({ data, totalScans = 0, className = '' }: PeakHoursChartProps) {
  const [chartType, setChartType] = useState<ChartType>('bar');

  // Create a map of all 24 hours with counts
  const hourMap = new Map<number, number>();
  (data || []).forEach(item => {
    hourMap.set(item.hour, item.count);
  });

  // Generate data for all 24 hours (0-23)
  const chartData = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    time: `${hour.toString().padStart(2, '0')}:00`,
    scans: hourMap.get(hour) || 0,
  }));

  // Calculate stats
  const stats = useMemo(() => {
    const maxScans = Math.max(...chartData.map(d => d.scans), 0);
    const peakHour = chartData.find(d => d.scans === maxScans);
    const total = chartData.reduce((sum, d) => sum + d.scans, 0);
    return { maxScans, peakHour, total };
  }, [chartData]);

  const hasAnyData = chartData.some(d => d.scans > 0);

  if (!hasAnyData && (!data || data.length === 0)) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#F59E0B]" />
            Peak Hours
          </h3>
        </div>
        <div className="h-48 flex items-center justify-center bg-[#F9FAFB] rounded-lg border border-dashed border-[#E5E7EB]">
          <div className="text-center">
            <Clock className="w-10 h-10 text-[#D1D5DB] mx-auto mb-2" />
            <p className="text-sm text-[#6B7280]">Hourly data will appear after scans</p>
          </div>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 10, left: 0, bottom: 0 },
    };

    if (chartType === 'line') {
      return (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="time"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, fill: '#6B7280' }}
            interval={5}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, fill: '#6B7280' }}
            tickFormatter={(value) => value.toLocaleString()}
            width={25}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) => [`${value} scans`, '']}
                labelFormatter={(label) => label}
              />
            }
          />
          <Line
            type="monotone"
            dataKey="scans"
            stroke="#F59E0B"
            strokeWidth={2}
            dot={{ fill: '#F59E0B', strokeWidth: 1, r: 2 }}
            activeDot={{ r: 4, stroke: '#F59E0B', strokeWidth: 2, fill: '#FFFFFF' }}
            animationBegin={200}
            animationDuration={1200}
            animationEasing="ease-out"
          />
        </LineChart>
      );
    }

    return (
      <BarChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
        <XAxis
          dataKey="time"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 9, fill: '#6B7280' }}
          interval={5}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 9, fill: '#6B7280' }}
          tickFormatter={(value) => value.toLocaleString()}
          width={25}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value) => [`${value} scans`, '']}
              labelFormatter={(label) => label}
            />
          }
        />
        <Bar
          dataKey="scans"
          fill="#F59E0B"
          radius={[2, 2, 0, 0]}
          animationBegin={200}
          animationDuration={1200}
          animationEasing="ease-out"
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.scans === 0 ? '#F3F4F6' : '#F59E0B'}
            />
          ))}
        </Bar>
      </BarChart>
    );
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#F59E0B]" />
            Peak Hours
          </h3>
          <p className="text-xs text-[#6B7280]">
            Peak: {stats.peakHour?.time || 'N/A'} ({stats.maxScans} scans)
          </p>
        </div>
        <ChartToggle chartType={chartType} onToggle={setChartType} />
      </div>

      <div className="w-full overflow-hidden">
        <ChartContainer config={chartConfig} className="h-36 w-full">
          {renderChart()}
        </ChartContainer>
      </div>

      {/* Compact Summary Stats */}
      <div className="mt-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <div>
            <span className="text-[#6B7280]">Peak: </span>
            <span className="font-medium text-[#111827]">{stats.maxScans}</span>
          </div>
          <div>
            <span className="text-[#6B7280]">Total: </span>
            <span className="font-medium text-[#111827]">{stats.total.toLocaleString()}</span>
          </div>
        </div>
        <div>
          <span className="text-[#6B7280]">Busiest: </span>
          <span className="font-medium text-[#111827]">{stats.peakHour?.time || 'N/A'}</span>
        </div>
      </div>
    </div>
  );
}
